'use client';

import { CampaignsInfo, CardPlayerInfo, Display, Gender, PlayerCareer } from '../types';
import Card from '../card/Card';
import fetcher, { Response } from '@/lib/utils/fetcherBasic';
import { useCallback, useEffect, useState } from 'react';
import { EdfCampaigns, EdfPlayers } from '@/lib/directus';
import TitleH2 from '../common/TitleH2';
import mapCampaignStaffToPlayerCard from '@/lib/mappers/mapCampaignStaffToPlayerCard';
import mapCampaignRosterToPlayerCard from '@/lib/mappers/mapCampaignRosterToPlayerCard';
import DirectusImage from '../image/DirectusImage';
import PlaceHolderFemaleMedalPicture from '../../../../public/png/placeholder_female_medal.png';
import PlaceHolderFemaleNonSelectedMedalPicture from '../../../../public/png/placeholder_female_medal_non_selected.png';
import PlaceHolderMaleMedalPicture from '../../../../public/png/placeholder_male_medal.png';
import PlaceHolderMaleNonSelectedMedalPicture from '../../../../public/png/placeholder_male_medal_non_selected.png';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import mapPlayersToPlayerCareer from '@/lib/mappers/mapPlayersToPlayerCareer';
import getDirectusImageUrl from '@/lib/utils/image';
import Modal from '../modal/Modal';

interface Props {
  data: CampaignsInfo;
  display: Display;
  title?: string;
  team_france?: boolean;
}

const Selections = {
  all: 'Sélections',
  '100': '100 et plus',
  '50': '50 et plus',
  '30': '30 et plus',
  '10': '10 et plus',
} as { [key: string]: string };

const Points = {
  all: 'Points marqués',
  '1000': '1000 et plus',
  '500': '500 et plus',
  '250': '250 et plus',
  '100': '100 et plus',
  '10': '10 et plus',
} as { [key: string]: string };

const GenderType = {
  all: 'Genre',
  '1': 'Masculin',
  '2': 'Féminin',
} as { [key: string]: string };

export default function PlayerCards({ data, display, title, team_france }: Props) {
  const pathname = usePathname();
  const [hash, setHash] = useState<string | null>(null);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [teamPhoto, setTeamPhoto] = useState<string | null>(null);
  const [playerCards, setPlayerCards] = useState<Array<CardPlayerInfo>>([]);
  const [playerCareers, setPlayerCareers] = useState<Array<PlayerCareer>>([]);
  const [staffCards, setStaffCards] = useState<Array<CardPlayerInfo>>([]);
  const [teamParams, setTeamParams] = useState<{
    teamName: string;
    year: number;
    competition: number;
  }>({
    teamName: data.defaultFilter.teamName || data.teamName,
    competition: data.defaultFilter.competition,
    year: data.defaultFilter.year,
  });
  const [playerParams, setPlayerParams] = useState<{
    gender: number;
    name: string;
    points: number;
    selections: number;
  }>({
    gender: 0,
    name: '',
    points: 0,
    selections: 0,
  });
  const [playerName, setPlayerName] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setHash(window.location.hash);
    }
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => {
      setPlayerParams((prevState) => {
        if (prevState.name === playerName) {
          return prevState;
        }
        return { ...prevState, name: playerName };
      });
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [playerName]);

  const isMultiGender = useCallback(() => {
    if (data.teamsDetails.length === 0) {
      return true;
    }

    const genderCounts = data.teamsDetails.reduce(
      (acc, team) => {
        if (team.gender === '1') acc.hasMale = true;
        if (team.gender === '2') acc.hasFemale = true;
        return acc;
      },
      { hasMale: false, hasFemale: false },
    );

    return genderCounts.hasMale && genderCounts.hasFemale;
  }, [data.teamsDetails]);

  const handlePlayerNameInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPlayerName(e.target.value);
  };

  const clearPlayerName = () => {
    setPlayerName('');
    setPlayerCareers([]);
  };

  const clearPlayerSelections = () => {
    setPlayerParams((prevState) => {
      return { ...prevState, selections: 0 };
    });
    setPlayerCareers([]);
  };

  const clearPlayerPoints = () => {
    setPlayerParams((prevState) => {
      return { ...prevState, points: 0 };
    });
    setPlayerCareers([]);
  };

  const clearPlayerGender = () => {
    setPlayerParams((prevState) => {
      return { ...prevState, gender: 0 };
    });
    setPlayerCareers([]);
  };

  const handleSelectTeamChanged = (teamName: string) => {
    const teamCompetitions = data.competitionsGroupedByTeam[teamName];

    if (!teamCompetitions) {
      setTeamPhoto(null);
      setPlayerCards([]);
      setStaffCards([]);
      setTeamParams((prevState) => {
        return { ...prevState, teamName };
      });
      return;
    }

    const competitionId = Object.keys(teamCompetitions)[0];
    const year = data.competitionsGroupedByTeam[teamName][competitionId][0];
    setTeamParams((prevState) => {
      return { ...prevState, teamName, competition: parseInt(competitionId), year };
    });
  };

  const handleSelectCompetitionChange = (competitionId: string) => {
    const year = data.competitionsGroupedByTeam[teamParams.teamName][competitionId][0];
    setTeamPhoto(data.competitionPhotosById[competitionId+'-'+year] || null);
    setTeamParams((prevState) => {
      return { ...prevState, competition: parseInt(competitionId), year };
    });
  };

  const handleSelectCompetitionYearChange = (year: string) => {
    setTeamPhoto(data.competitionPhotosById[teamParams.competition+'-'+year] || null);
    setTeamParams((prevState) => {
      return { ...prevState, year: parseInt(year) };
    });
  };

  const handleSelectPlayerSelectionsChange = (selections: string) => {
    setPlayerParams((prevState) => {
      return { ...prevState, selections: parseInt(selections) };
    });
  };

  const handleSelectPlayerPointsChange = (points: string) => {
    setPlayerParams((prevState) => {
      return { ...prevState, points: parseInt(points) };
    });
  };

  const handleSelectPlayerGenderChange = (gender: string) => {
    setPlayerParams((prevState) => {
      return { ...prevState, gender: parseInt(gender) };
    });
  };

  const fetchPlayers = useCallback(async () => {
    let queryParams = ``;

    if (playerParams.name || playerParams.name.trim() !== '') {
      queryParams += `name=${playerParams.name}`;
    }

    if (playerParams.selections > 0) {
      queryParams += queryParams.length
        ? `&selections=${playerParams.selections}`
        : `selections=${playerParams.selections}`;
    } else {
      queryParams += queryParams.length ? `&selections=${1}` : `selections=${1}`;
    }

    if (playerParams.points > 0) {
      queryParams += queryParams.length
        ? `&points=${playerParams.points}`
        : `points=${playerParams.points}`;
    }

    if (playerParams.gender > 0) {
      queryParams += queryParams.length
        ? `&gender=${playerParams.gender}`
        : `gender=${playerParams.gender}`;
    }

    if (data.teamsDetails.length) {
      queryParams += queryParams.length
        ? `&teams=${data.teamsDetails.map(({ id }) => id)}`
        : `teams=${data.teamsDetails.map(({ id }) => id)}`;
    }

    if (!queryParams) {
      return;
    }

    queryParams += `&limit=-1`;

    setLoading(true);
    const response = await fetcher<Array<EdfPlayers>>(`/api/player?${queryParams}`);

    if ((response as Response).message === 'error') {
      setLoading(false);
    } else {
      const players = response as Array<EdfPlayers>;
      let playerCareerInfo = mapPlayersToPlayerCareer(players);
      if (playerParams.points > 1) {
        playerCareerInfo = playerCareerInfo.sort((a, b) => b.playerPoints - a.playerPoints);
      } else if (playerParams.selections > 0) {
        playerCareerInfo = playerCareerInfo.sort((a, b) => b.playerSelections - a.playerSelections);
      }
      setPlayerCareers(playerCareerInfo);
    }
    setLoading(false);
  }, [playerParams, data.teamsDetails]);

  const fetchCampaignComposition = async (
    teamName: string,
    teamGender: Gender,
    competition: number,
    year: number,
  ) => {
    if (!teamName || (!teamName && competition < 0) || (!team_france && year < 0)) {
      return;
    }
    let queryParams = `?team=${teamName}&competition=${competition}&year=${year}`;
    if (team_france) {
      queryParams = `?team=${teamName}`;
    }
    setLoading(true);
    const response = await fetcher<Array<EdfCampaigns>>(`/api/campaign${queryParams}`);

    if ((response as Response).message === 'error') {
      setLoading(false);
    } else {
      const campaigns = response as Array<EdfCampaigns>;

      if (campaigns.length > 0) {
        const playerCards = mapCampaignRosterToPlayerCard(campaigns[0].roster, teamGender);
        
        setPlayerCards(playerCards);

        const staffCards = mapCampaignStaffToPlayerCard(campaigns[0].staff);
        setTeamPhoto(data.competitionPhotosById[competition+'-'+year] || null);
        setStaffCards(staffCards);
        
      }
      setLoading(false);
    }
  };

  useEffect(() => {
    if (display !== 'search') {
      fetchCampaignComposition(
        teamParams.teamName,
        data.genderByTeam[teamParams.teamName],
        teamParams.competition,
        teamParams.year,
      );
    }
  }, [display, teamParams.teamName, teamParams.competition, teamParams.year, data.genderByTeam]);

  useEffect(() => {
    if (display === 'search'){
      fetchPlayers();
    }
  }, [display, playerParams, fetchPlayers]);

  const teamNames = data.teamsDetails.map((team) => team.name || '');
  const teamCompetitionTypes = data.competitionsGroupedByTeam[teamParams.teamName]
    ? Object.keys(data.competitionsGroupedByTeam[teamParams.teamName])
    : [];
  const teamCompetitionYears = data.competitionsGroupedByTeam[teamParams.teamName]
    ? data.competitionsGroupedByTeam[teamParams.teamName][teamParams.competition]
    : [];

  const currentSelectedTeam = data.teamsDetails.find((team) => team.name === teamParams.teamName);

  const HistoryLinkWrapper = (
    children: React.ReactNode,
    historyUrl: string,
    index: number,
  ): JSX.Element => {
    if (currentSelectedTeam?.age_category !== 'S') {
      return <div key={index}>{children}</div>;
    }
    return (
      <Link key={index} href={historyUrl} scroll={false} replace={true}>
        {children}
      </Link>
    );
  };

  const FullPlayersDisplay = () => {
    return (
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3 lg:grid-cols-4 clear-both">
        {playerCards.map(
          (
            {
              birthDate,
              callToActionUrl,
              clubName,
              fullName,
              height,
              image,
              imageAlt,
              man,
              number,
              position,
              playerId,
              selected,
              title,
            },
            index,
          ) => {
            let historyUrl = `/${pathname.split('/')[1]}/history-player/${playerId}?playerId=${playerId}`;
            if (playerId && playerId > 0) {
              historyUrl += `&fromPlayerId=${playerId}`;
            }
            if (hash) {
              historyUrl += hash;
            }
            return HistoryLinkWrapper(
              <Card
                title={title}
                type="team"
                callToActionUrl={callToActionUrl}
                image={image}
                imageAlt={imageAlt}
                height={height}
                birthDate={birthDate}
                fullName={fullName}
                clubName={clubName}
                position={position}
                selected={selected}
                man={man}
                imageSizes="(min-width: 66em) 17vw, (min-width: 44em) 33vw, 100vw"
                number={number}
              />,
              historyUrl,
              index,
            );
          },
        )}
      </div>
    );
  };

  const FullStaffDisplay = () => {
    return (
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3 lg:grid-cols-4 clear-both">
        {staffCards.map(
          (
            { title, fullName, callToActionUrl, clubName, image, position, man, number, selected },
            index,
          ) => (
            <Card
              key={index}
              title={title}
              type="team"
              callToActionUrl={callToActionUrl}
              image={image}
              fullName={fullName}
              clubName={clubName}
              position={position}
              selected={selected}
              man={man}
              imageSizes="(min-width: 66em) 17vw, (min-width: 44em) 33vw, 100vw"
              number={number}
            ></Card>
          ),
        )}
      </div>
    );
  };

  const TableStaffDisplay = () => {
    return (
      <div className="relative overflow-x-auto">
        <div className="min-w-max">
          <div className="relative h-[40px] rounded-md bg-gradient-to-r from-blue-900 via-blue-900 to-red-600 pb-3.5 pl-[83px] pt-4 opacity-20"></div>
          <div className="absolute top-[15px] inline-flex items-start justify-start gap-[17px] text-[11px] font-bold uppercase leading-[9.90px] text-slate-950 opacity-100">
            <div className="flex w-[70px] items-center justify-center lg:w-[180px]" />
            <span className="w-[215px]">Prénom & Nom</span>
            <span className="w-[150px]">Rôle</span>
            <span>Club</span>
          </div>
          <div className="pl-0 lg:pl-[83px]">
            {staffCards.map(({ fullName, clubName, image, position, man, selected }, index) => {
              let imagePlaceHolder = man
                ? PlaceHolderMaleMedalPicture
                : PlaceHolderFemaleMedalPicture;
              if (!selected) {
                imagePlaceHolder = man
                  ? PlaceHolderMaleNonSelectedMedalPicture
                  : PlaceHolderFemaleNonSelectedMedalPicture;
              }
              return (
                <div
                  key={index}
                  className="flex h-[59px] items-center justify-start gap-4 text-sm font-bold text-gray-400 lg:h-[69px]"
                >
                  <div className="flex w-[70px] items-center justify-center lg:w-[100px]">
                    <div className="h-[40px] w-[40px]">
                      <DirectusImage
                        src={image ? image : imagePlaceHolder}
                        dimension={{ width: 40, height: 40 }}
                        alt={''}
                      />
                    </div>
                  </div>
                  <span className="w-[215px] text-slate-950">{fullName}</span>
                  <span className="w-[150px]">{position}</span>
                  <span>{clubName}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const TablePlayersDisplay = () => {
    const isSearch = display === 'search';
    return (
      <div className="relative overflow-x-auto clear-both">
        <div className="min-w-max">
          <div className="relative h-[40px] rounded-md bg-gradient-to-r from-blue-900 via-blue-900 to-red-600 pb-3.5 pl-[83px] pt-4 opacity-20"></div>
          <div className="absolute top-[15px] inline-flex items-start justify-start gap-[17px] text-[11px] font-bold uppercase leading-[9.90px] text-slate-950 opacity-100">
            {!isSearch && (
              <>
                <div className="flex w-[70px] items-center justify-center lg:w-[180px]" />
                <span className="text-center lg:w-[94px]">Maillot</span>
                <span className="w-[215px]">Prénom & Nom</span>
                <span className="w-[150px]">Position</span>
                <span className="w-[67px]">Taille</span>
                <span className="w-[67px]">Naissance</span>
                <span>Club</span>
              </>
            )}

            {isSearch && (
              <>
                <span className="ml-0 w-[300px] lg:ml-[83px]">Prénom & Nom</span>
                <span className="w-[215px]">Sélections</span>
                <span className="w-[215px]">Points</span>
                <span className="w-[215px]">Taille</span>
              </>
            )}
          </div>
          {isSearch && !loading && playerCareers.length === 0 && (
            <span className="my-4 flex min-h-[300px] items-start justify-start gap-4 text-sm">
              Aucun résultat, veuillez modifier vos critères de recherche
            </span>
          )}
          <div className="pl-0 lg:pl-[83px]">
            {!isSearch &&
              playerCards.map(
                (
                  {
                    birthDate,
                    fullName,
                    height,
                    clubName,
                    image,
                    position,
                    man,
                    number,
                    selected,
                    playerId,
                  },
                  index,
                ) => {
                  let imagePlaceHolder = man
                    ? PlaceHolderMaleMedalPicture
                    : PlaceHolderFemaleMedalPicture;
                  if (!selected) {
                    imagePlaceHolder = man
                      ? PlaceHolderMaleNonSelectedMedalPicture
                      : PlaceHolderFemaleNonSelectedMedalPicture;
                  }

                  let historyUrl = `/${pathname.split('/')[1]}/history-player/${playerId}?playerId=${playerId}`;
                  if (playerId && playerId > 0) {
                    historyUrl += `&fromPlayerId=${playerId}`;
                  }
                  if (hash) {
                    historyUrl += hash;
                  }

                  return HistoryLinkWrapper(
                    <div className="flex h-[59px] items-center justify-start gap-4 text-sm font-bold text-gray-400 lg:h-[69px]">
                      <div className="flex w-[70px] items-center justify-center lg:w-[100px]">
                        <div className="h-[40px] w-[40px]">
                          <DirectusImage
                            src={image ? image : imagePlaceHolder}
                            dimension={{ width: 40, height: 40 }}
                            alt={''}
                          />
                        </div>
                      </div>

                      <span
                        className={`w-[50px] text-center font-AgencyFBBlackComp text-[35px] font-normal uppercase leading-loose tracking-wide lg:w-[94px] ${selected ? 'text-red-600' : 'text-gray-400'} antialiased`}
                      >
                        {typeof number !== 'undefined' && number >= 0 ? (number || '0') : ''}
                      </span>
                      <span className="w-[215px] text-slate-950">{fullName}</span>
                      <span className="w-[150px]">{position}</span>
                      <span className="w-[67px]">{height}</span>
                      <span className="w-[67px]">{birthDate}</span>
                      <span>{clubName}</span>
                    </div>,
                    historyUrl,
                    index,
                  );
                },
              )}

            {isSearch &&
              playerCareers.map(
                ({ id: playerId, firstName, lastName, height, playerSelections, playerPoints, maj_n_npj }, index) => {
                  let historyUrl = `/${pathname.split('/')[1]}/history-player/${playerId}?playerId=${playerId}`;
                  if (playerId && playerId > 0) {
                    historyUrl += `&fromPlayerId=${playerId}`;
                  }
                  if (hash) {
                    historyUrl += hash;
                  }

                  let heightPlayer = ' - ';
                  if (height) {
                    let heightPlayerNumber = height;
                    if (heightPlayerNumber > 100) {
                      heightPlayer = (heightPlayerNumber/100).toFixed(2)+'m';
                    } else {
                      heightPlayer = heightPlayerNumber.toFixed(2)+'m';
                    }
                  }
                  return HistoryLinkWrapper(
                    <div className="flex h-[59px] items-center justify-start gap-4 text-sm font-bold text-gray-400 lg:h-[69px]">
                      <span className="w-[300px] text-slate-950">{`${firstName} ${lastName}`}</span>
                      <span className="w-[215px]">{playerSelections}</span>
                      <span className="w-[215px]">{maj_n_npj ? 'NPJ' : playerPoints}</span>
                      <span className="w-[215px]">{heightPlayer}</span>
                    </div>,

                    historyUrl,
                    index,
                  );
                },
              )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="mb-20 min-h-[800px] w-full py-10 lg:p-10">
      <div className="flex flex-wrap gap-2.5">
        {display !== 'search' && (
          <>
            {teamNames.length > 1 && (
              <select
                className="select select-bordered max-w-xs rounded-[100px] border-zinc-200 text-sm font-bold focus:outline-none"
                value={teamParams.teamName}
                onChange={(e) => handleSelectTeamChanged(e.target.value)}
                disabled={playerParams.name !== '' || playerParams.selections > 0}
              >
                {teamNames.map((teamName, index) => {
                  return (
                    <option key={index} value={teamName}>
                      {teamName}
                    </option>
                  );
                })}
              </select>
            )}
            {teamCompetitionTypes.length > 0 && (
              <select
                className="select select-bordered max-w-xs rounded-[100px] border-zinc-200 text-sm font-bold focus:outline-none"
                onChange={(e) => handleSelectCompetitionChange(e.target.value)}
                value={teamParams.competition}
                disabled={playerParams.name !== '' || playerParams.selections > 0}
              >
                {teamCompetitionTypes.map((competitionId, index) => {
                  return (
                    <option key={index} value={competitionId}>
                      {data.competitionNamesById[competitionId]}
                    </option>
                  );
                })}
              </select>
            )}
            {teamCompetitionYears && teamCompetitionYears.length > 0 && (
              <select
                className="select select-bordered max-w-xs rounded-[100px] border-zinc-200 text-sm font-bold focus:outline-none"
                onChange={(e) => handleSelectCompetitionYearChange(e.target.value)}
                value={teamParams.year}
                disabled={playerParams.name !== '' || playerParams.selections > 0}
              >
                {teamCompetitionYears.map((year, index) => {
                  return (
                    <option key={index} value={year}>
                      {year}
                    </option>
                  );
                })}
              </select>
            )}
          </>
        )}
        {display === 'search' && (
          <>
            <div className="relative flex">
              <input
                type="text"
                placeholder="Nom"
                className="input input-bordered w-full rounded-[100px] text-sm font-bold text-slate-950 placeholder-slate-950 outline-none hover:text-black focus:text-black focus:outline-none lg:w-72 lg:max-w-lg lg:pr-11"
                value={playerName}
                onChange={handlePlayerNameInputChange}
              />
              {playerParams.name && (
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 transform text-gray-500 hover:text-gray-700 focus:outline-none"
                  onClick={clearPlayerName}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}
            </div>
            {isMultiGender() && (
              <div className="relative flex">
                <select
                  className="select select-bordered max-w-xs rounded-[100px] border-zinc-200 pr-[60px] text-sm font-bold focus:outline-none"
                  onChange={(e) => handleSelectPlayerGenderChange(e.target.value)}
                  value={playerParams.gender}
                >
                  {Object.keys(GenderType)
                    .reverse()
                    .map((key, index) => {
                      return (
                        <option key={index} value={key}>
                          {GenderType[key]}
                        </option>
                      );
                    })}
                </select>
                {playerParams.gender > 0 && (
                  <button
                    type="button"
                    className="absolute right-8 top-1/2 -translate-y-1/2 transform text-gray-500 hover:text-gray-700 focus:outline-none"
                    onClick={clearPlayerGender}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-6 w-6"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                )}
              </div>
            )}
            <div className="relative flex">
              <select
                className="select select-bordered max-w-xs rounded-[100px] border-zinc-200 pr-[60px] text-sm font-bold focus:outline-none"
                onChange={(e) => handleSelectPlayerSelectionsChange(e.target.value)}
                value={playerParams.selections}
              >
                {Object.keys(Selections)
                  .reverse()
                  .map((key, index) => {
                    return (
                      <option key={index} value={key}>
                        {Selections[key]}
                      </option>
                    );
                  })}
              </select>
              {playerParams.selections > 0 && (
                <button
                  type="button"
                  className="absolute right-8 top-1/2 -translate-y-1/2 transform text-gray-500 hover:text-gray-700 focus:outline-none"
                  onClick={clearPlayerSelections}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}
            </div>
            <div className="relative flex">
              <select
                className="select select-bordered max-w-xs rounded-[100px] border-zinc-200 pr-[60px] text-sm font-bold focus:outline-none"
                onChange={(e) => handleSelectPlayerPointsChange(e.target.value)}
                value={playerParams.points}
              >
                {Object.keys(Points)
                  .reverse()
                  .map((key, index) => {
                    return (
                      <option key={index} value={key}>
                        {Points[key]}
                      </option>
                    );
                  })}
              </select>
              {playerParams.points > 0 && (
                <button
                  type="button"
                  className="absolute right-8 top-1/2 -translate-y-1/2 transform text-gray-500 hover:text-gray-700 focus:outline-none"
                  onClick={clearPlayerPoints}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}
            </div>
          </>
        )}
      </div>
      {display !== 'search' && teamPhoto &&
      <button
          onClick={() => setShowModal(!showModal)}
          className={`cursor-pointer md:w-1/3 block relative rounded-md overflow-hidden mt-[20px] md:mt-[-50px] mb-[20px] md:float-right z-1`}
        >
        <DirectusImage
          src={getDirectusImageUrl(teamPhoto)}
          objectFit="cover"
          objectPosition={''}
          alt={''}
          styles="max-w-[100%]"
        />
      </button>}
      {loading && (
        <span className="my-4 flex min-h-[300px] items-center justify-center gap-4 text-sm">
          Chargement en cours<span className="loading loading-dots loading-lg"></span>
        </span>
      )}
      {!loading && (playerCards.length >= 0 || playerCareers.length >= 0) && (
        <div>
          <div className="my-7">
            <TitleH2 color="black" size="medium" customFont={true}>
              {!title && (
                <>
                  {display !== 'search' &&
                    (data.teamGender === Gender.Male ? 'Les joueurs' : 'Les joueuses')}
                  {display === 'search' && 'Les joueur-euses'}
                </>
              )}
              {title && title}
            </TitleH2>
          </div>
          {display === 'full' && FullPlayersDisplay()}
          {display !== 'full' && TablePlayersDisplay()}
        </div>
      )}
      {!loading && !playerParams.name && !playerParams.selections && staffCards.length > 0 && (
        <div>
          {display !== 'search' && (
            <div className="my-7">
              <TitleH2 color="black" size="medium" customFont={true}>
                Staff
              </TitleH2>
            </div>
          )}
          {display === 'full' && FullStaffDisplay()}
          {display === 'simple' && TableStaffDisplay()}
        </div>
      )}
      {display !== 'search' && teamPhoto && showModal && 
          <Modal
            closeFunction={() => setShowModal(false)}
          >
            <DirectusImage
              src={getDirectusImageUrl(teamPhoto)}
              objectFit="cover"
              objectPosition={''}
              alt={''}
              styles="max-w-[100%] m-auto"
            />
          </Modal>
      }
    </div>
  );
}
